import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { RadioGroup, RadioGroupItem, Label, cn } from '@scio3/ui';
import { ADDON_TIER_CATALOG, ADDON_TIER_FEATURE_KEYS } from '../utils/billing-constants';

type Tier = 'standard' | 'pro';

type TierPricing = {
  /** Price per screen per interval, numeric */
  price: number;
  /** Interval label fragment, e.g. "mo" or "yr" */
  intervalSuffix: string;
};

type Props = {
  addonKey: string;
  selectedTier: Tier;
  onTierChange: (tier: Tier) => void;
  pricing: Partial<Record<Tier, TierPricing>>;
  currencySymbol: string;
  /** Tier the account is currently subscribed to — renders the "Current Plan" indicator */
  currentTier?: Tier | null;
  disabled?: boolean;
};

/**
 * Standard vs Pro tier radio cards for multi-tier add-ons.
 * Renders null when the add-on has no tier catalog entry, so non-tiered
 * add-ons (aericast, videowall) are unaffected.
 *
 * Visual style matches PlanComparisonTable: teal-banner header on the selected
 * card, "Most Popular" pill on Pro, split-format price, and Check-icon feature
 * lists. Selection is signalled by the header colour + border + elevation; the
 * radio input itself is sr-only so the affordance stays in sync with the rest
 * of the subscription UI.
 */
export function AddonTierRadioCards({
  addonKey,
  selectedTier,
  onTierChange,
  pricing,
  currencySymbol,
  currentTier = null,
  disabled = false,
}: Props) {
  const { t } = useTranslation();
  const tiers = ADDON_TIER_CATALOG[addonKey];
  const featureKeys = ADDON_TIER_FEATURE_KEYS[addonKey];

  if (!tiers || !featureKeys) return null;

  const headingId = `addon-tier-cards-heading-${addonKey}`;

  return (
    <div className="space-y-3" data-testid="addon-tier-cards">
      <h3 id={headingId} className="text-xl font-semibold text-center">
        {t('Choose your plan')}
      </h3>
      <RadioGroup
        value={selectedTier}
        onValueChange={(value) => onTierChange(value as Tier)}
        disabled={disabled}
        aria-labelledby={headingId}
        className="grid grid-cols-2 gap-4 pt-3 max-w-lg mx-auto"
      >
        {tiers.map(({ tier }) => {
          const tierPricing = pricing[tier];
          const bullets = featureKeys[tier] ?? [];
          const isSelected = selectedTier === tier;
          const isCurrent = currentTier === tier;
          const tierLabel = tier === 'pro' ? t('Pro') : t('Standard');

          // Split price into dollars + cents to match PlanComparisonTable's
          // typographic hierarchy (small currency / large integer / smaller cents).
          const price = tierPricing?.price ?? 0;
          const priceDollars = Math.floor(price);
          const priceCents = (price % 1).toFixed(2).slice(2);

          return (
            <Label
              key={tier}
              htmlFor={`addon-tier-${tier}`}
              data-testid={`addon-tier-card-${tier}`}
              className={cn(
                'relative flex flex-col rounded-lg border bg-card transition-colors min-h-[360px]',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary',
                isSelected
                  ? 'border-primary shadow-[0_12px_24px_-6px_rgba(0,0,0,0.18)]'
                  : 'border-border',
              )}
            >
              {/* Header — teal banner when the card is selected. Negative margins
                  pull the fill over the 1px border so the colour reaches the rounded
                  corner without a sliver showing through (mirrors PlanComparisonTable). */}
              <div
                className={cn(
                  'px-4 pt-7 pb-4 text-center rounded-t-lg -mt-px -mx-px',
                  isSelected ? 'bg-primary text-white' : 'bg-transparent',
                )}
              >
                <h4
                  className={cn(
                    'text-lg font-semibold',
                    isSelected ? 'text-white' : 'text-foreground',
                  )}
                >
                  {tierLabel}
                </h4>
                {isCurrent && (
                  <p
                    className={cn(
                      'mt-1 text-xs font-medium',
                      isSelected ? 'text-white/90' : 'text-muted-foreground',
                    )}
                  >
                    {t('Current Plan')}
                  </p>
                )}
              </div>

              {/* Price — currency / dollars-period / cents stack */}
              {tierPricing && (
                <div className="px-4 py-4 text-center">
                  <div className="flex items-start justify-center">
                    <span className="text-sm text-primary mt-2">{currencySymbol}</span>
                    <span className="text-4xl font-semibold text-primary leading-none">
                      {priceDollars}.
                    </span>
                    <span className="text-lg text-primary mt-1">{priceCents}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {tierPricing.intervalSuffix === 'yr'
                      ? t('per screen/year')
                      : t('per screen/month')}
                  </p>
                </div>
              )}

              {/* Features list */}
              {bullets.length > 0 && (
                <div className="flex-1 px-4 pb-4">
                  <ul className="space-y-1.5">
                    {bullets.map((key) => (
                      <li
                        key={key}
                        className="flex items-start gap-2 text-sm text-muted-foreground"
                      >
                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
                        <span>{t(key)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Radio input is sr-only — selection is conveyed by the header
                  colour + border + shadow, identical to PlanComparisonTable. */}
              <RadioGroupItem
                value={tier}
                id={`addon-tier-${tier}`}
                aria-label={tierLabel}
                className="sr-only"
              />
            </Label>
          );
        })}
      </RadioGroup>
    </div>
  );
}
